import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faAward, faUsers, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import AboutUsImage from "../assets/about.jpg"

const AboutSection = styled.section`
  padding: 100px 0;
  background-color: #fff;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
  
  @media (max-width: 576px) {
    padding: 50px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 576px) {
    padding: 0 15px;
  }
`;

const AboutWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const AboutImage = styled(motion.div)`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  filter: drop-shadow(4px 4px 8px rgba(0, 0, 0, 0.6));
  
  &:before {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border: 10px solid #ff6e6e;
    top: -30px;
    left: -30px;
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border: 10px solid #ff6e6e;
    bottom: -30px;
    right: -30px;
    z-index: -1;
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
  }
  
  @media (max-width: 992px) {
    margin-bottom: 30px;
  }
  
  @media (max-width: 576px) {
    &:before, &:after {
      width: 70px;
      height: 70px;
      border-width: 7px;
    }
    
    &:before {
      top: -20px;
      left: -20px;
    }
    
    &:after {
      bottom: -20px;
      right: -20px;
    }
  }
`;

const AboutContent = styled.div``;

const SectionTitle = styled.div`
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    margin-bottom: 25px;
  }
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -5px;
    width: 50px;
    height: 2px;
    background-color: #ff6e6e;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    letter-spacing: 1.5px;
    margin-bottom: 12px;
    
    &:after {
      width: 40px;
      height: 1.5px;
    }
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.2;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
    margin-bottom: 15px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    font-size: 15px;
    line-height: 1.7;
    margin-bottom: 25px;
  }
`;

const FeatureList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 16px;
  color: #333;
  
  svg {
    color: #ff6e6e;
    margin-right: 10px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    margin-bottom: 12px;
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 30px;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    padding: 15px;
  }
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: rgba(255, 94, 20, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  color: #ff6e6e;
  font-size: 24px;
`;

const StatCount = styled.h3`
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const StatTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  
  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

// const Button = styled(motion.a)`
//   display: inline-block;
//   padding: 15px 30px;
//   background-color: #ff6e6e;
//   color: #fff;
//   border-radius: 4px;
//   font-weight: 500;
//   text-decoration: none;
//   transition: all 0.3s ease;
//   border: 2px solid #ff6e6e;
  
//   &:hover {
//     background-color: transparent;
//     color: #ff6e6e;
//   }
  
//   @media (max-width: 576px) {
//     padding: 12px 25px;
//     font-size: 14px;
//     display: block;
//     text-align: center;
//   }
// `;

const featureVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.1 * i,
      duration: 0.5,
    }
  })
};

const statVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2 * i,
      duration: 0.5,
    }
  })
};

const About = () => {
  return (
    <AboutSection id="about">
      <Container>
        <AboutWrapper>
          <AboutImage
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={AboutUsImage} alt="About Us" />
          </AboutImage>
          
          <AboutContent>
            <SectionTitle>
              <SubTitle
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                About Us
              </SubTitle>
              <Title
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Crafting <span>Scalable Software</span> for {new Date().getFullYear() - 2018}+ Years
              </Title>
            </SectionTitle>
            
            <Description
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Founded in 2018, Zenitech Solutions is a global IT solutions provider committed to delivering innovative and high-performance digital products. From web and mobile app development to cloud services and beyond, we specialize in crafting customized solutions that drive business growth and digital transformation.

            </Description>
            
            <FeatureList
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                'Innovative design solutions tailored to your brand',
                'Expert development team with diverse skill sets',
                'Strategic marketing approaches for maximum ROI',
                'Dedicated support and maintenance services'
              ].map((feature, index) => (
                <FeatureItem
                  key={index}
                  custom={index}
                  variants={featureVariants}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  {feature}
                </FeatureItem>
              ))}
            </FeatureList>
            
            {/* <Button
              href="#services"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Our Services
            </Button> */}
            
            <StatsContainer
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <StatItem custom={0} variants={statVariants}>
                <StatIcon>
                  <FontAwesomeIcon icon={faAward} />
                </StatIcon>
                <StatCount>500+</StatCount>
                <StatTitle>Projects Completed</StatTitle>
              </StatItem>
              
              <StatItem custom={1} variants={statVariants}>
                <StatIcon>
                  <FontAwesomeIcon icon={faUsers} />
                </StatIcon>
                <StatCount>90+</StatCount>
                <StatTitle>Happy Clients</StatTitle>
              </StatItem>
              
              <StatItem custom={2} variants={statVariants}>
                <StatIcon>
                  <FontAwesomeIcon icon={faProjectDiagram} />
                </StatIcon>
                <StatCount>15+</StatCount>
                <StatTitle>Servers</StatTitle>
              </StatItem>
            </StatsContainer>
          </AboutContent>
        </AboutWrapper>
      </Container>
    </AboutSection>
  );
};

export default About;